import { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  Chip,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import { useRecordContext, useNotify, useRefresh } from "react-admin";
import { apiUrl } from "../../../httpClient";
import { handleApiError } from "../../../utils/errorHandler";
import { ADMIN_BASE } from "../../../config";
import type {
  BusinessRulesResponseDto,
  UpdateBusinessRulesDto,
  Environment,
} from "../../../types/api";

export const ProviderBusinessRules = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [businessRules, setBusinessRules] = useState<
    BusinessRulesResponseDto | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedEnv, setSelectedEnv] = useState<Environment>("production");
  const [formData, setFormData] = useState<UpdateBusinessRulesDto>({
    markup: undefined,
    timeout: undefined,
    retryAttempts: undefined,
    policies: {},
  });

  useEffect(() => {
    if (record?.id) {
      loadBusinessRules();
    }
  }, [record?.id, selectedEnv]);

  useEffect(() => {
    if (businessRules) {
      setFormData({
        markup: businessRules.markup,
        timeout: businessRules.timeout,
        retryAttempts: businessRules.retryAttempts,
        policies: businessRules.policies || {},
      });
    }
  }, [businessRules]);

  const loadBusinessRules = async () => {
    if (!record?.id) return;

    setLoading(true);
    try {
      const response = await fetch(
        apiUrl(
          `${ADMIN_BASE}/providers/${record.id}/business-rules${
            selectedEnv ? `?environment=${selectedEnv}` : ""
          }`,
        ),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        // API returns array, get first item or use object directly
        const rules = Array.isArray(data) ? data[0] || null : data;
        setBusinessRules(rules);
      } else if (response.status === 404) {
        setBusinessRules(null);
      }
    } catch (error) {
      const appError = handleApiError(error);
      notify(appError.message, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!record?.id) return;

    setSaving(true);
    try {
      const updateData: UpdateBusinessRulesDto = {
        ...formData,
        environment: selectedEnv,
      };

      const response = await fetch(
        apiUrl(`${ADMIN_BASE}/providers/${record.id}/business-rules`),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
          },
          body: JSON.stringify(updateData),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update business rules");
      }

      notify("Business rules updated successfully", { type: "success" });
      loadBusinessRules();
      refresh();
    } catch (error) {
      const appError = handleApiError(error);
      notify(appError.message, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (!record) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Environment</InputLabel>
          <Select
            value={selectedEnv}
            onChange={(e) => setSelectedEnv(e.target.value as Environment)}
            label="Environment"
          >
            <MenuItem value="development">Development</MenuItem>
            <MenuItem value="staging">Staging</MenuItem>
            <MenuItem value="production">Production</MenuItem>
          </Select>
        </FormControl>
        <Button
          startIcon={<Save />}
          onClick={handleSave}
          variant="contained"
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Rules"}
        </Button>
      </Box>

      {loading ? (
        <Typography>Loading business rules...</Typography>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField
              label="Markup (%)"
              type="number"
              value={formData.markup || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  markup: e.target.value ? parseFloat(e.target.value) : undefined,
                })
              }
              fullWidth
              helperText="Percentage markup applied to provider prices"
            />

            <TextField
              label="Timeout (ms)"
              type="number"
              value={formData.timeout || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  timeout: e.target.value ? parseInt(e.target.value) : undefined,
                })
              }
              fullWidth
              helperText="Request timeout in milliseconds"
            />

            <TextField
              label="Retry Attempts"
              type="number"
              value={formData.retryAttempts || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  retryAttempts: e.target.value
                    ? parseInt(e.target.value)
                    : undefined,
                })
              }
              fullWidth
              helperText="Number of retry attempts for failed requests"
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Policies (JSON)
              </Typography>
              <TextField
                multiline
                rows={8}
                value={JSON.stringify(formData.policies || {}, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    setFormData({ ...formData, policies: parsed });
                  } catch {
                    // Invalid JSON, keep as is
                  }
                }}
                fullWidth
                helperText="Additional policies as JSON object"
                error={
                  formData.policies !== undefined &&
                  typeof formData.policies !== "object"
                }
              />
            </Box>

            {businessRules && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Last updated: {new Date(businessRules.updatedAt).toLocaleString()}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};
